import React, { useState, useEffect, useRef } from 'react';

import { ScrollArea } from "@/components/ui/scroll-area";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import { Calendar as CalendarIcon } from "lucide-react";

import { useRecoilState } from 'recoil';
import { outletAtom, subAtom } from 'core/config/atoms';

import { motion } from "framer-motion";
import moment from 'moment';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

import ServiceReport from 'services/serviceReport';
import ServicePrinter from 'services/servicePrinter';

import TopBar from 'core/widgets/ui/TopBar';

export default function PanelInvoice(props) {

    const [outlet] = useRecoilState(outletAtom);
    const [sub] = useRecoilState(subAtom);

    const [orders, setOrders] = useState([]);

    const [filter, setFilter] = useState({});
    const [ord, setOrd] = useState({});

    const [date, setDate] = useState({
        from: moment().startOf('month').toDate(),
        to: moment().toDate(),
    });

    const [tab, setTab] = useState('all');
    const [query, setQuery] = useState('');

    const [pageSize, setPageSize] = useState(10);
    const [pageCurrent, setPageCurrent] = useState(0);
    const [pageTotal, setPageTotal] = useState(0);

    const [isDate, setIsDate] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    const reportService = new ServiceReport();
    const printService = new ServicePrinter();

    const changeTab = (val) => {
        setTab(val);
        setQuery('');
        setDate({
            from: moment().startOf('year').toDate(),
            to: moment().toDate(),
        });
        switch (val) {
            case "all":
                setFilter({});
                break;
            case "paid":
                setFilter({ 'status': 'paid' });
                break;
            case "cancel":
                setFilter({ 'status': 'cancel' });
                break;
            default:
                break;
        }
    }

    const handleOnChange = (e) => {
        var { value, name } = e.target;
        switch (name) {
            case 'pageSize':
                setPageSize(value);
                break;
            case 'search':
                setQuery(value);
                break;
        }
    }

    const onDateChange = (event) => {
        setIsDate(true);
        setDate(event);
    }

    const openPanel = (order) => {
        setOrd(order);
        setIsOpen(true);
    }

    const getRoundOff = (ordTemp) => {
        const finalTotal = (ordTemp.total - ordTemp.discount) + ordTemp.tax + ordTemp.scharge;
        const roundedTotal = Math.round(finalTotal);
        const roundOffValue = (roundedTotal - finalTotal).toFixed(2);
        if (roundOffValue === "0.00") {
            return roundOffValue;
        }
        return roundOffValue > 0 ? `+${roundOffValue}` : roundOffValue;
    }

    const getTotal = (ordItems) => {
        let total = 0;
        ordItems.forEach((a) => {
            total = total + (a.price * a.qty);
        });
        return total;
    }

    const getDiscount = (ordTemp) => {
        let dis = 0;
        if (parseFloat(ordTemp.discount) !== 0) {
            if (ordTemp.dmode === "A") {
                dis = parseFloat(ordTemp.discount);
            } else {
                dis = getTotal(ordTemp.items) * (parseFloat(ordTemp.discount) / 100);
            }
        }
        return dis;
    }

    const getChipColorA = (status) => {
        switch (status) {
            case 'open':
                return "bg-blue-100 text-blue-800";
            case 'running':
                return "bg-yellow-100 text-yellow-800";
            case 'paid':
                return "bg-green-100 text-green-800";
            case 'cancel':
                return "bg-red-100 text-red-800";
            default:
                return " bg-gray-100 text-gray-800";
        }
    }

    const printOption = (ordid) => {
        var body = {
            "id": ordid,
            "outlet": outlet.outletid.outletid
        }
        printService.printBill(body, '').then((res) => {
            toast.success("Order has sent to print!", { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
        });
    }

    const downloadFile = ({ data, fileName, fileType }) => {
        const blob = new Blob([data], { type: fileType })
        const a = document.createElement('a')
        a.download = fileName
        a.href = window.URL.createObjectURL(blob)
        const clickEvt = new MouseEvent('click', {
            view: window,
            bubbles: true,
            cancelable: true,
        })
        a.dispatchEvent(clickEvt)
        a.remove()
    }

    const exportData = (type) => {
        (async function () {
            let body = {
                "filter": filter,
                "page": 0,
                "size": 0,
                "searchQuery": query,
                "startDate": isDate ? date.from.toISOString() : "",
                "endDate": isDate ? moment(date.to).add(1, 'day').toDate().toISOString() : ""
            };
            var res = await reportService.getAllInvoice(body, '');
            var records = res.data.data;
            let items = [];
            records.forEach((item, index) => {
                items.push({
                    "Sr No.": (index + 1),
                    "Invoice Number": item.invid,
                    "Invoice date": item.createdAt,
                    "Total": item.total,
                    "Discount": item.discount,
                    "CGST (2.5%)": item.tax / 2,
                    "SGST (2.5%)": item.tax / 2,
                    "Service Charge": item.scharge,
                    "Grand Total": item.grand,
                    "Payment Mode": item.pmode,
                    "Status": item.status,
                    "createdAt": item.createdAt,
                });
            });

            if (type === "xlsx") {
                const worksheet = XLSX.utils.json_to_sheet(items);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "invoice");
                XLSX.writeFile(workbook, "invoice.xlsx");
            } else {
                let headers = ['Invoice Number, Invoice date, Total, Discount, CGST (2.5%), SGST (2.5%), Service Charge, Grand Total, Payment Mode, Status, createdAt'];
                let recordCsv = [];
                items.map((record) => {
                    recordCsv.push(`${record["Invoice Number"]}, ${record["createdAt"]}, ${record["Total"]}, ${record["Discount"]}, ${record["CGST (2.5%)"]}, ${record["SGST (2.5%)"]}, ${record["Service Charge"]}, ${record["Grand Total"]},${record["Payment Mode"]}, ${record["Status"]}, ${record["createdAt"]}`);
                });
                downloadFile({
                    data: [...headers, ...recordCsv].join('\n'),
                    fileName: 'invoice.csv',
                    fileType: 'text/csv',
                });
            }
        })();
    }

    const pageChange = (pageValue) => {
        (async function () {
            let body = {
                "filter": filter,
                "page": pageValue,
                "size": parseInt(pageSize),
                "searchQuery": query,
                "startDate": isDate ? date.from.toISOString() : "",
                "endDate": isDate ? moment(date.to).add(1, 'day').toDate().toISOString() : ""
            };
            var res = await reportService.getAllInvoice(body, '');
            if (res['status']) {
                setPageCurrent(res.data.currentPage);
                setPageTotal(res.data.totalPage);
                setOrders(res.data.data);
            } else {
                toast.error('Server error please try again', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
            }
        })();
    }

    const loadData = () => {
        (async function () {
            let body = {
                "filter": filter,
                "page": 1,
                "size": parseInt(pageSize),
                "searchQuery": query,
                "startDate": isDate ? date.from.toISOString() : "",
                "endDate": isDate ? moment(date.to).add(1, 'day').toDate().toISOString() : ""
            };
            var res = await reportService.getAllInvoice(body, '');
            if (res['status']) {
                setPageCurrent(res.data.currentPage);
                setPageTotal(res.data.totalPage);
                setOrders(res.data.data);
            } else {
                toast.error('Server error please try again', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
            }
        })();
    }

    const nextPage = () => {
        if (pageCurrent < pageTotal) {
            pageChange(pageCurrent + 1);
        }
    }

    const prevPage = () => {
        if (pageCurrent > 1) {
            pageChange(pageCurrent - 1);
        }
    }

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        loadData();
    }, [pageSize, query, date, filter]);

    return (
        <>
            {
                isOpen && <div className='overflow-y-auto overflow-x-hidden z-[100] w-full h-modal md:h-full fixed top-0 left-0 bg-black bg-opacity-70'>
                    <div className='bg-white w-[600px] min-h-screen absolute right-0 p-[20px]'>
                        <ScrollArea>
                            <div className='px-4'>
                                <div className='flex items-center justify-between text-2xl'>
                                    <h1 className='font-sans font-medium text-gray-900 uppercase'>Invoice Bill #{ord.invid}</h1>
                                    <span onClick={() => {
                                        setIsOpen(false);
                                        setOrd({});
                                    }}><i className="las la-times text-xl text-gray-700 cursor-pointer"></i></span>
                                </div>
                                <div className='flex mt-4'>
                                    <p className='font-sans font-medium'>Order Type:
                                        <span className={`ml-4 px-3 inline-flex text-xs leading-5 font-semibold rounded-full uppercase bg-blue-100 text-blue-800`}>
                                            Food Bill
                                        </span>
                                    </p>
                                    <p className='ml-8 font-sans font-medium'>Status:
                                        <span className={`ml-4 px-4 inline-flex text-xs leading-5 font-semibold rounded-full uppercase ${getChipColorA(ord.status)}`}>
                                            {ord.status}
                                        </span>
                                    </p>
                                </div>
                                <div className='mt-2'>
                                    Timestamp: <b>{moment(ord.createdAt).format('DD MMM, yyyy hh:mm:ss A')}</b>
                                </div>
                                <hr className='my-2' />
                                <div className='p-2 bg-gray-200 w-full text-sm font-medium rounded mt-4 flex justify-between items-center'>
                                    <span className='flex items-center'>
                                        <i className="las la-file-invoice mr-2 text-xl"></i>Items Summery
                                    </span>
                                </div>
                                <div className='mt-4 flex items-center'>
                                    <p className='text-sm font-bold text-gray-950'>Item Name</p>
                                    <div className='flex-grow'></div>
                                    <p className='text-sm font-bold text-gray-950'>Item Unit</p>
                                </div>
                                <hr className='my-2' />
                                {
                                    ord.items.map((item) => {
                                        return <div className='flex items-center'>
                                            <p className='text-sm  font-medium text-gray-950'>{item.name}</p>
                                            <div className='flex-grow'></div>
                                            <p className='text-sm font-medium'>{item.qty} x ₹ {item.price} </p>
                                        </div>
                                    })
                                }
                                <hr className='my-2' />
                                <div className="w-full mt-2 space-y-1">
                                    <div className="flex justify-between">
                                        <span className="font-medium text-sm">Subtotal</span>
                                        <span className="font-medium text-sm">₹ {getTotal(ord.items).toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="font-medium text-sm">@ Discount ({ord.dmode === "A" ? "₹" : "%"})</span>
                                        <span className="font-medium text-sm">₹ {getDiscount(ord)}</span>
                                    </div>
                                    {
                                        sub.features.isTax && <div className="flex justify-between">
                                            <span className="font-medium text-sm">Tax (₹)</span>
                                            <span className="font-medium text-sm">₹ {ord.tax.toFixed(2)}</span>
                                        </div>
                                    }
                                    <div className="flex justify-between">
                                        <span className="font-medium text-sm">Service Charges</span>
                                        <span className="font-medium text-sm">₹ {ord.scharge.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="font-medium text-sm">Round off</span>
                                        <span className="font-medium text-sm">₹ {getRoundOff(ord)}</span>
                                    </div>
                                    <div className="border-t-2 mt-2 py-2 flex items-center justify-between">
                                        <span className="font-medium text-lg">Total</span>
                                        <span className="font-medium text-lg">₹ {ord.grand.toFixed(2)}</span>
                                    </div>
                                </div>
                                <div className='flex mt-4'>
                                    <div className="py-2 rounded-md shadow-lg text-center bg-prime text-white font-semibold cursor-pointer flex-grow" onClick={() => {
                                        printOption(ord._id);
                                    }}>
                                        PRINT ORDER
                                    </div>
                                </div>
                            </div>
                        </ScrollArea >
                    </div>
                </div>
            }
            <ScrollArea className="w-full h-full px-[4px]">
                <div className="grid grid-cols-12 grid-gap-2">
                    <div className="col-span-12 mt-2">
                        <div className="intro-y flex items-center justify-between h-10 border-b pb-4">
                            <h2 className="text-2xl font-medium font-serif truncate ml-2 text-ascent">
                                <motion.i whileHover={{ scale: 1.2 }} className="las la-bars cursor-pointer" onClick={() => { props.onToggle(); }}></motion.i> Invoice Tally
                            </h2>
                            <TopBar />
                        </div>
                        <div className="w-full mt-2 flex justify-between items-center">
                            <div className='h-10 bg-gray-100 rounded'>
                                <ul className="intro-y flex text-sm font-sans">
                                    <li className={`px-3 h-8 m-1 rounded flex items-center justify-center ${tab === 'all' ? 'text-red-600 font-medium bg-white shadow' : 'text-asent font-medium'}`}><button className='' onClick={() => { changeTab('all') }}>All</button></li>
                                    <li className={`px-3 h-8 m-1 rounded flex items-center justify-center ${tab === 'paid' ? 'text-red-600 font-medium bg-white shadow' : 'text-asent font-medium'}`}><button className='' onClick={() => { changeTab('paid') }}>Paid</button></li>
                                    <li className={`px-3 h-8 m-1 rounded flex items-center justify-center ${tab === 'cancel' ? 'text-red-600 font-medium bg-white shadow' : 'text-asent font-medium'}`}><button className='' onClick={() => { changeTab('cancel') }}>Cancelled</button></li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-span-12 mt-2">
                    <div className="w-full shadow-md rounded-md border">
                        <table className="min-w-full divide-y divide-gray-200 px-4">
                            <thead className="bg-gray-50">
                                <tr className=''>
                                    <th scope="col" className="border-b border-gray-200" colSpan={sub.features.isTax ? 9 : 8}>
                                        <div className='w-full flex items-center space-x-2 px-2'>
                                            <form className="py-2 flex-grow flex items-center">
                                                <div className='flex'>
                                                    <Popover>
                                                        <PopoverTrigger asChild>
                                                            <Button id="date" variant={"outline"}
                                                                className={cn(
                                                                    "w-[300px] justify-start text-left font-normal",
                                                                    !date && "text-muted-foreground"
                                                                )}
                                                            >
                                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                                {date?.from ? (
                                                                    date.to ? (
                                                                        <>
                                                                            {moment(date.from).format('MMM DD, yyyy')} -{" "}
                                                                            {moment(date.to).format('MMM DD, yyyy')}
                                                                        </>
                                                                    ) : (
                                                                        moment(date.from).format('MMM DD, yyyy')
                                                                    )
                                                                ) : (
                                                                    <span>Pick a date</span>
                                                                )}
                                                            </Button>
                                                        </PopoverTrigger>
                                                        <PopoverContent className="w-auto p-0" align="start">
                                                            <Calendar
                                                                initialFocus
                                                                mode="range"
                                                                defaultMonth={date?.from}
                                                                selected={date}
                                                                onSelect={onDateChange}
                                                                numberOfMonths={2}
                                                            />
                                                        </PopoverContent>
                                                    </Popover>
                                                </div>
                                                <div className='w-full flex items-center'>
                                                    <div className='w-[80px]'>
                                                        <p>Search :</p>
                                                    </div>
                                                    <input type="text" value={query} onChange={handleOnChange} name='search' placeholder="Search by invoice number." className="w-full h-10 px-4 font-sans text-sm outline-none rounded border" />
                                                </div>
                                            </form>
                                            <div className='flex items-center'>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger>
                                                        <button className="w-full py-1 px-2 h-10 rounded-md bg-prime hover:bg-primeLight text-white mr-4"><i className="las la-file-download mr-2"></i> Export </button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent>
                                                        <div
                                                            className='bg-white shadow-md rounded-md absolute' style={{ top: '0px', left: '-64px' }}>
                                                            <div>
                                                                <ul className='flex flex-col items-start p-2'>
                                                                    <DropdownMenuItem>
                                                                        <li className="w-40 cursor-pointer p-2 rounded-md" onClick={() => {
                                                                            exportData('xlsx');
                                                                        }}>
                                                                            <div className='flex items-center text-base'>
                                                                                <p className="text-sm font-serif ml-2 font-normal">Export to .xlsx</p>
                                                                            </div>
                                                                        </li>
                                                                    </DropdownMenuItem>
                                                                    <DropdownMenuItem>
                                                                        <li className="w-40 cursor-pointer p-2 rounded-md" onClick={() => {
                                                                            exportData('csv');
                                                                        }}>
                                                                            <div className='flex items-center text-base'>
                                                                                <p className="text-sm font-serif ml-2 font-normal">Export to .csv</p>
                                                                            </div>
                                                                        </li>
                                                                    </DropdownMenuItem>
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </div>
                                        </div>
                                    </th>
                                </tr>
                                <tr>
                                    <th scope="col" className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                        INVOICE NUM
                                    </th>
                                    <th scope="col" className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                        INVOICE Items
                                    </th>
                                    <th scope="col" className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                        TOTAL
                                    </th>
                                    <th scope="col" className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                        DISCOUNT
                                    </th>
                                    {
                                        sub.features.isTax && <th scope="col" className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                            TAX
                                        </th>
                                    }
                                    <th scope="col" className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                        SCHARGE
                                    </th>
                                    <th scope="col" className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                        INVOICE TOTAL
                                    </th>
                                    <th scope="col" className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                        STATUS
                                    </th>
                                    <th scope="col" className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                        Created At
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {
                                    orders.length !== 0 && orders.map((order) => {
                                        return <tr className="hover:bg-gray-100">
                                            <td className="p-3 whitespace-nowrap cursor-pointer">
                                                <div className="text-sm font-bold text-gray-900 cursor-pointer" onClick={() => { openPanel(order) }}>
                                                    <div className='flex items-center text-prime'>
                                                        {order.invid}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="p-3 whitespace-nowrap">
                                                <div className="text-sm font-bold text-gray-900">
                                                    {order.items.length} x Items
                                                </div>
                                            </td>
                                            <td className="p-3 whitespace-nowrap">
                                                <div className="text-sm text-prime">
                                                    ₹ {getTotal(order.items).toFixed(2)}
                                                </div>
                                            </td>
                                            <td className="p-3 whitespace-nowrap">
                                                <div className="text-sm text-prime">
                                                    ₹ {getDiscount(order).toFixed(2)}
                                                </div>
                                            </td>
                                            {
                                                sub.features.isTax && <td className="p-3 whitespace-nowrap">
                                                    <div className="text-sm text-prime">
                                                        ₹ {order.tax.toFixed(2)}
                                                    </div>
                                                </td>
                                            }
                                            <td className="p-3 whitespace-nowrap">
                                                <div className="text-sm text-prime">
                                                    ₹ {order.scharge.toFixed(2)}
                                                </div>
                                            </td>
                                            <td className="p-3 whitespace-nowrap">
                                                <div className="text-sm text-prime">
                                                    ₹ {order.grand.toFixed(2)}
                                                </div>
                                            </td>
                                            <td className="p-3 whitespace-nowrap">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full uppercase ${getChipColorA(order.status)}`}>
                                                    {order.status.toUpperCase()}
                                                </span>
                                            </td>
                                            <td className="p-3 whitespace-nowrap">
                                                <div className="text-sm text-blue-600">
                                                    {moment(order.createdAt).format('DD MMM, yyyy hh:mm:ss A')}
                                                </div>
                                            </td>
                                        </tr>
                                    })
                                }
                                {
                                    orders.length === 0 && <tr className="cursor-pointer hover:bg-gray-100">
                                        <td className="p-3 whitespace-nowrap" colSpan={sub.features.isTax ? 9 : 8}>
                                            <div className="text-sm text-red-600 text-center">
                                                No data found.
                                            </div>
                                        </td>
                                    </tr>
                                }
                            </tbody>
                        </table>
                        <div className='flex py-2 px-4 items-center bg-blue-50'>
                            <p className='text-sm font-serif'>Show rows per page</p>
                            <select className='text-sm ml-2 bg-gray-100' name="pageSize" onChange={handleOnChange} value={pageSize}>
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={40}>40</option>
                                <option value={50}>50</option>
                            </select>
                            <div className='flex-grow'></div>
                            <p className='text-sm font-serif mr-2'>Page {pageCurrent} of {pageTotal}</p>
                            <div onClick={() => { prevPage() }}><i className={`las la-angle-left w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-200 ${pageCurrent <= 1 ? 'text-gray-400 cursor-not-allowed' : 'text-black cursor-pointer'}`}></i></div>
                            <div onClick={() => { nextPage() }}><i className={`las la-angle-right w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-200 ml-2 text-black ${pageCurrent === pageTotal ? 'text-gray-400 cursor-not-allowed' : 'text-black cursor-pointer'}`}></i></div>
                        </div>
                    </div>
                </div>
            </ScrollArea>
        </>
    )
}
